'use client'

/**
 * Global Error Boundary
 *
 * Catches errors at the root level, including errors in the layout itself.
 * This is a last-resort fallback that provides minimal UI without any dependencies.
 *
 * NOTE: Hardcoded hex colors are intentional — this component renders when the
 * layout (and globals.css) may have failed to load, so CSS variables are unavailable.
 */

import { useEffect } from 'react'

type GlobalErrorProps = {
  error: Error & { digest?: string }
  reset: () => void
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Log comprehensive error details
    console.error('Global error caught:', {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      name: error.name,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    })
  }, [error])

  return (
    <html lang="en">
      <head>
        <title>Application Error - Konstantout</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body
        style={{
          margin: 0,
          padding: 0,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          backgroundColor: '#0a0a0b',
          color: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <div
          style={{
            maxWidth: '500px',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          {/* Error Icon */}
          <div
            style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
            }}
          >
            ⚠️
          </div>

          {/* Title */}
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '0.75rem',
            }}
          >
            Critical Application Error
          </h1>

          {/* Description */}
          <p
            style={{
              fontSize: '0.875rem',
              color: '#a1a1aa',
              marginBottom: '1.5rem',
              lineHeight: '1.5',
            }}
          >
            The application encountered a critical error and cannot continue.
            Please check the browser console for details.
          </p>

          {/* Error Message */}
          <div
            style={{
              backgroundColor: '#18181b',
              border: '1px solid #27272a',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#ef4444',
                marginBottom: '0.5rem',
              }}
            >
              Error:
            </div>
            <pre
              style={{
                fontSize: '0.75rem',
                color: '#a1a1aa',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {error.message}
            </pre>
            {error.digest && (
              <div
                style={{
                  fontSize: '0.75rem',
                  color: '#71717a',
                  marginTop: '0.5rem',
                }}
              >
                Error ID: {error.digest}
              </div>
            )}
          </div>

          {/* Stack Trace (only in dev) */}
          {process.env.NODE_ENV === 'development' && error.stack && (
            <details
              style={{
                marginBottom: '1.5rem',
                textAlign: 'left',
              }}
            >
              <summary
                style={{
                  fontSize: '0.75rem',
                  color: '#a1a1aa',
                  cursor: 'pointer',
                  marginBottom: '0.5rem',
                }}
              >
                Stack trace (development only)
              </summary>
              <pre
                style={{
                  fontSize: '0.625rem',
                  color: '#71717a',
                  backgroundColor: '#18181b',
                  padding: '0.75rem',
                  borderRadius: '0.25rem',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}
              >
                {error.stack}
              </pre>
            </details>
          )}

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={reset}
              style={{
                backgroundColor: '#18181b',
                color: '#fafafa',
                border: '1px solid #27272a',
                padding: '0.625rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = '#27272a')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = '#18181b')
              }
            >
              🔄 Try again
            </button>

            <button
              onClick={() => {
                localStorage.clear()
                sessionStorage.clear()
                window.location.href = '/'
              }}
              style={{
                backgroundColor: '#ef4444',
                color: '#fafafa',
                border: 'none',
                padding: '0.625rem 1.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = '#dc2626')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = '#ef4444')
              }
            >
              Reset application
            </button>
          </div>

          {/* Help Text */}
          <p
            style={{
              fontSize: '0.75rem',
              color: '#71717a',
              marginTop: '1.5rem',
            }}
          >
            Open the browser console (F12) to see detailed error information.
          </p>
        </div>
      </body>
    </html>
  )
}
